import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import { ProgressBarSingle } from "components/DesignComponents/ProgressBar/ProgressBarSingle";
import ParamsContext from "context/params/paramsContext";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import { useApi } from "shared/hooks/useApi";
import { useFormattedNumber } from "shared/hooks/useFormattedNumber";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { ENDPOINTS, FILTER_NAMES } from "shared/types/enums";

import { RESOURCES } from "../types";
import { initialData } from "./initialData";

const LimitContainer = () => {
    const { blue500, gray200, red700, red800 } = colors;

    const { queryParams } = useContext(ParamsContext);

    const { t } = useTranslation();
    const { renderFormattedNumber } = useFormattedNumber();
    const { cancelSource, getData, handleResponse, isCanceled } = useApi();
    const isMounted = useIsMounted();

    const [isLoading, setIsLoading] = useState(true);
    const [limitData, setLimitData] = useState(initialData);

    const rootCompanyId = queryParams[FILTER_NAMES.RootCompany];

    useEffect(() => {
        const getLimitData = async () => {
            try {
                setIsLoading(true);

                const {
                    data: { resource_limits }
                } = await getData(
                    `${ENDPOINTS.Companies}/${rootCompanyId}/resourceLimits`
                );

                if (isMounted) {
                    // Sort the resource limits data based on a predefined order
                    const sortedData: any[] = resource_limits.sort(
                        (a: any, b: any) => {
                            const order = [
                                RESOURCES.Companies,
                                RESOURCES.Users,
                                RESOURCES.ConfigurationFiles
                            ];

                            return (
                                order.indexOf(a.type) - order.indexOf(b.type)
                            );
                        }
                    );

                    setLimitData(sortedData);
                }
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    handleResponse(error);
                    setLimitData(initialData);
                }
            }

            setIsLoading(false);
        };

        getLimitData();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, [rootCompanyId]);

    return (
        <div
            css={css({
                "& > div + div": {
                    marginTop: "16px"
                }
            })}
        >
            {limitData.map(({ type, current, limit }) => {
                const isUnlimited = limit === -1;
                const isLimitReached = limit !== 0 && current >= limit;

                const value = isUnlimited
                    ? renderFormattedNumber(current)
                    : `${renderFormattedNumber(current)} / ${renderFormattedNumber(limit)}`;

                const limitName = t(
                    `Dashboard##company level##resource limit types##${type}`
                );

                const title = `${limitName}:`;

                return (
                    <ProgressBarSingle
                        key={type}
                        id={type}
                        totalCount={isUnlimited ? current : limit}
                        filledCount={current}
                        filledColor={isLimitReached ? red700 : blue500}
                        totalColor={gray200}
                        titleLeft={title}
                        titleRight={isLoading ? "" : value}
                        titleRightColor={isLimitReached ? red800 : undefined}
                        isLoading={isLoading}
                    />
                );
            })}
        </div>
    );
};

export default LimitContainer;
